'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { trackGovernmentTenderInteraction, trackVarahaPartnershipInteraction, trackBehaviorFlow } from '@/lib/analytics';

interface ServicesSectionProps {
  currentLanguage: string;
}

const ServicesSection = ({ currentLanguage }: ServicesSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleServiceClick = (serviceTitle: string, link: string) => {
    // Track service interactions for SEO and conversion pathways
    if (link === '/enterprise-government-solutions') {
      trackGovernmentTenderInteraction('service_click', serviceTitle);
    }
    if (link === '/let-s-collaborate') {
      trackVarahaPartnershipInteraction('service_click', serviceTitle);
    }
    trackBehaviorFlow('service_navigation', window.location.pathname, link);
  };

  // Use only English content
  const content = {
    en: {
      title: 'Our Comprehensive ESG Services',
      subtitle: 'End-to-end sustainability solutions for enterprises, value chain partners, and government initiatives',
      nicheTitle: 'Niche ESG Services',
      nicheSubtitle: 'Specialized sustainability solutions with cutting-edge technology integration',
      nicheServices: [
        {
          icon: 'FireIcon',
          title: 'Fugitive Emissions Tracing & Remediation',
          description: 'Advanced detection and remediation of fugitive emissions using satellite-based monitoring and AI-powered analytics for industrial facilities.',
          features: ['Leak Detection', 'Real-time Monitoring', 'Remediation Planning', 'Compliance Reporting']
        },
        {
          icon: 'SunIcon',
          title: 'Accredited Solar Assessor Services',
          description: 'Certified solar energy assessment and optimization services for commercial and industrial installations with performance guarantees.',
          features: ['Site Assessment', 'Energy Modeling', 'ROI Analysis', 'Performance Monitoring']
        },
        {
          icon: 'BeakerIcon',
          title: 'Soil & Water Analysis',
          description: 'Comprehensive soil health and water quality analysis for agricultural and industrial applications with actionable remediation strategies.',
          features: ['Soil Health Testing', 'Water Quality Analysis', 'Contamination Assessment', 'Remediation Solutions']
        },
        {
          icon: 'GlobeAltIcon',
          title: 'Satellite-Based Mineral Analysis with ECLIPSE',
          description: 'Advanced satellite-based mineral exploration and analysis integrated with ECLIPSE software for precision resource mapping and sustainability assessment.',
          features: ['Mineral Mapping', 'ECLIPSE Integration', 'Resource Assessment', 'Environmental Impact']
        },
        {
          icon: 'CpuChipIcon',
          title: 'AI Framework for White Spot Disease Prevention',
          description: 'Revolutionary AI-based framework solution preventing White Spot Disease that accounted for 80% loss in prawn farming over the last 3 years, protecting aquaculture investments.',
          features: ['Disease Detection', 'Predictive Analytics', 'Loss Prevention', 'Farm Optimization']
        },
        {
          icon: 'DocumentTextIcon',
          title: 'BRSR Lite & Sustainability Roadmap for Value Chain Partners',
          description: 'BRSR Lite and Sustainability Roadmap definition for Value Chain partners and vendors for UK, EU, Australia International Vendor empanelment automation with AI.',
          features: ['BRSR Lite Filing', 'Vendor Empanelment AI', 'International Compliance', 'Sustainability Roadmap']
        },
        {
          icon: 'ServerIcon',
          title: 'ESG Metrics with Financial ROI & Green Credit for Data Centers',
          description: 'Comprehensive ESG metrics tracking with financial ROI analysis and Green Credit generation specifically designed for data center operations and sustainability.',
          features: ['ESG Metrics Tracking', 'Financial ROI Analysis', 'Green Credit Generation', 'Data Center Optimization']
        }
      ],
      services: [
        {
          icon: 'DocumentChartBarIcon',
          title: 'BRSR & FIBAC Reporting for Top 500 Companies',
          description: 'Karnataka Bank and enterprise BRSR compliance, FIBAC reporting, and comprehensive NetZero roadmap creation with AI-powered gap analysis.',
          features: ['BRSR Core & Extended', 'FIBAC Compliance', 'NetZero Roadmap', 'AI Gap Analysis'],
          link: '/brsr-esg-compliance-hub'
        },
        {
          icon: 'SparklesIcon',
          title: 'ESG Scope 1, 2, 3 Assessment',
          description: 'Complete greenhouse gas inventory for pharma, manufacturing, and enterprise - from emissions reporting to low emission strategies.',
          features: ['Scope 1/2/3 Calculation', 'Industry Emission Reporting', 'Low Emission Strategies', 'GHG Protocol Compliance'],
          link: '/ai-powered-emission-calculation-platform'
        },
        {
          icon: 'BuildingOffice2Icon',
          title: 'Value Chain & CSRD Certification',
          description: 'Support value chain service providers with BRSR Lite filing, CSRD adherence, Scope 3 data capture, and spend-to-activity transition analysis.',
          features: ['BRSR Lite Filing', 'CSRD Certification', 'Scope 3 Assessment', 'Spend Analysis'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'CurrencyDollarIcon',
          title: 'Carbon Credit MRV & Generation',
          description: 'Advanced carbon credit algorithms with AI and Quantum IP for government initiatives, private enterprises, and plantation owners - including seacoast and water body rejuvenation.',
          features: ['MRV Validation', 'Credit Registration', 'Earning Acceleration', 'Blockchain Verification'],
          link: '/carbon-credits-explained'
        },
        {
          icon: 'BeakerIcon',
          title: 'Sustainable Food & Cosmetics Operations',
          description: 'Heritage Foods, RPG Biochar, Makhana sustainable food & cosmetics, and export/import operations with ESG compliance.',
          features: ['Sustainable Food Systems', 'Biochar Solutions', 'Export Compliance', 'Supply Chain ESG'],
          link: '/industry-specific-esg-solutions'
        },
        {
          icon: 'CpuChipIcon',
          title: 'Data Center Carbon Footprint Reduction',
          description: 'Comprehensive data center sustainability with energy optimization, renewable integration, and carbon footprint tracking for IT infrastructure.',
          features: ['Energy Optimization', 'Renewable Integration', 'PUE Monitoring', 'Green IT Strategy'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'SunIcon',
          title: 'Renewable Energy & Carbon Credits',
          description: 'Solar power transmission, wind energy, hydrogen AMC with AI and Quantum IP - including carbon credit generation for clean energy projects.',
          features: ['Solar/Wind Projects', 'Hydrogen Solutions', 'AI Optimization', 'Credit Generation'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'WrenchScrewdriverIcon',
          title: 'Value Chain Mining & Processing',
          description: 'Sustainable mining operations with upstream emission tracking, EHS SAP expertise, and supply chain optimization for oil and mining sectors.',
          features: ['Mining ESG', 'Upstream Emissions', 'EHS SAP Integration', 'Supply Chain ESG'],
          link: '/industry-specific-esg-solutions'
        },
        {
          icon: 'GlobeAltIcon',
          title: 'International GHG Reporting',
          description: 'Global greenhouse gas reporting for value chain partners with CDP, GRI, TCFD compliance and international sustainability frameworks.',
          features: ['CDP Reporting', 'GRI Standards', 'TCFD Framework', 'Multi-jurisdiction'],
          link: '/esg-reporting-solutions'
        },
        {
          icon: 'HomeModernIcon',
          title: 'Smart Farming & Soil Rejuvenation',
          description: 'Desert and soil rejuvenation with IoT/AI strategies, smart farming technologies, and sustainable agriculture practices.',
          features: ['IoT Agriculture', 'Soil Health Monitoring', 'Desert Restoration', 'AI Crop Management'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'WaterIcon',
          title: 'Water Recycling & Blue Credits',
          description: 'STP optimization, water recycling in AP/TG, large water body conservation, and blue credit generation for sustainable water management.',
          features: ['STP Optimization', 'Blue Credits', 'Water Conservation', 'Quality Monitoring'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'HomeIcon',
          title: 'Municipal Lake & Ecosystem Conservation',
          description: 'Municipal lake conservation in Madhya Pradesh, marsh land rejuvenation in Finland, and ecosystem restoration collaboration.',
          features: ['Lake Conservation', 'Wetland Restoration', 'Biodiversity Monitoring', 'Community Engagement'],
          link: '/enterprise-government-solutions'
        },
        {
          icon: 'TruckIcon',
          title: 'Sustainable Supply Chain & Transportation',
          description: 'Comprehensive supply chain sustainability with transportation emission tracking, logistics optimization, and green fleet management.',
          features: ['Fleet Optimization', 'Route Planning', 'Modal Shift Analysis', 'Last-Mile Solutions'],
          link: '/industry-specific-esg-solutions'
        },
        {
          icon: 'CubeIcon',
          title: 'Fisheries AI & IoT Enhancement',
          description: 'AI framework AquaVeritas™ to detect, prevent and segregate White Spot Disease (WSDD) and 13 other diseases preventing Indian shrimp farmers from meeting export criteria to Australia and Europe. Solutions for individual farms and turnkey enterprises with trainings and human+AI+robotics setup with quality traceable AI pipeline for export quality clearance.',
          features: ['AquaVeritas™ AI Framework', 'Disease Detection & Prevention', 'Export Quality Compliance', 'Human+AI+Robotics Setup'],
          link: '/industry-specific-esg-solutions'
        },
        {
          icon: 'DocumentTextIcon',
          title: 'Study of ESG, Carbon Credit Feasibility',
          description: 'Comprehensive ESG and Carbon Credit feasibility studies for Private, Government, and Public bodies - from assessment to implementation roadmap.',
          features: ['ESG Feasibility', 'Carbon Credit Assessment', 'Public Sector Studies', 'Implementation Planning'],
          link: '/our-services'
        }
      ]
    }
  };

  const lang = content.en; // Always use English

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Niche ESG Services Section */}
        <div className="mb-16 md:mb-20">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-block px-6 py-2 bg-accent/10 rounded-full mb-4">
              <p className="text-sm font-semibold text-accent">
                {currentLanguage === 'en' ? 'Specialized Solutions' : 
                 currentLanguage === 'hi' ? 'विशेष समाधान' : 
                 currentLanguage === 'ar' ? 'حلول متخصصة' : 
                 currentLanguage === 'te' ? 'ప్రత్యేక పరిష్కారాలు' : 'Specialized Solutions'}
              </p>
            </div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-text-primary">
              {lang.nicheTitle || 'Niche ESG Services'}
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              {lang.nicheSubtitle || 'Specialized sustainability solutions with cutting-edge technology integration'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
            {lang.nicheServices && lang.nicheServices.map((service: any, index: number) => (
              <div
                key={index}
                onMouseEnter={() => isHydrated && setHoveredCard(index + 1000)}
                onMouseLeave={() => isHydrated && setHoveredCard(null)}
                className="bg-gradient-to-br from-accent/5 to-secondary/5 rounded-xl border-2 border-accent/20 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic overflow-hidden group hover:border-accent/40"
              >
                <div className="p-8 space-y-6">
                  <div
                    className={`w-16 h-16 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isHydrated && hoveredCard === index + 1000
                        ? 'bg-accent text-accent-foreground scale-110'
                        : 'bg-accent/10 text-accent'
                    }`}
                  >
                    <Icon name={service.icon as any} size={32} />
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-heading font-bold text-xl text-text-primary">
                      {service.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed text-sm">
                      {service.description}
                    </p>
                  </div>

                  <ul className="space-y-2">
                    {service.features.map((feature: string, featureIndex: number) => (
                      <li key={featureIndex} className="flex items-start space-x-2">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          className="text-accent flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Services Section */}
        <div className="text-center space-y-4 mb-8 md:mb-12">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-text-primary">
            {lang.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {lang.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {lang.services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => isHydrated && setHoveredCard(index)}
              onMouseLeave={() => isHydrated && setHoveredCard(null)}
              className="bg-card rounded-xl border border-border shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic overflow-hidden group"
            >
              <div className="p-8 space-y-6">
                <div
                  className={`w-16 h-16 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    isHydrated && hoveredCard === index
                      ? 'bg-primary text-primary-foreground scale-110'
                      : 'bg-surface text-primary'
                  }`}
                >
                  <Icon name={service.icon as any} size={32} />
                </div>

                <div className="space-y-3">
                  <h3 className="font-heading font-bold text-xl text-text-primary">
                    {service.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>

                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2">
                      <Icon
                        name="CheckCircleIcon"
                        size={20}
                        className="text-success flex-shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  href={service.link}
                  className="inline-flex items-center text-primary font-semibold text-sm hover:text-primary/80 transition-colors duration-200"
                >
                  {currentLanguage === 'hi' ? 'और जानें' : currentLanguage === 'ar' ? 'اعرف المزيد' : currentLanguage === 'te' ? 'మరింత తెలుసుకోండి' : 'Learn More'}
                  <Icon name="ArrowRightIcon" size={16} className="ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Services CTA */}
        <div className="text-center mt-12">
          <Link
            href="/our-services"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 shadow-natural hover:shadow-natural-lg"
          >
            {currentLanguage === 'hi' ? 'सभी सेवाएं देखें' : currentLanguage === 'ar' ? 'عرض جميع الخدمات' : currentLanguage === 'te' ? 'అన్ని సేవలను చూడండి' : 'View All Our Services'}
            <Icon name="ArrowRightIcon" size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;